export interface ReportAnswerKPI {
  id: number;
  documentId: string;
  answer: string;
  month: string;
  year: number;
  factory: {
    id: number;
    documentId: string;
    title: string;
  };
  direction: {
    id: number;
    documentId: string;
    Title: string;
  };
  gop: {
    id: number;
    documentId: string;
    title: string;
  };
  requirement: {
    id: number;
    documentId: string;
    Title: string;
    isFocal: boolean | null;
  };
}

export interface AnswerQuestionKPI {
  answer: 'Да' | 'Нет' | 'N/A';
  department: string;
  factory?: string;
  month?: string;
  year?: number;
  direction?: string;
  requirement?: string;
  isFocal?: boolean;
  answerId?: number | null;
}

export interface ProcessedKPIData {
  data: Array<{ value: number; label: string } | null>;
  kpiPlan: Array<{ value: number; label: string }>;
  kpiFact: Array<{ value: number; label: string }>;
}

export interface KPIFilters {
  factories?: string[];
  months?: string[];
  years?: string[];
  directions?: string[];
  gops?: string[];
  isFocal?: boolean;
  blocks?: string[];
  departments?: string[];
  kpiNames?: string[];
}

export const MONTH_NAMES = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

export const formatMonth = (month: string): string => {
  const monthIndex = parseInt(month) - 1;
  return MONTH_NAMES[monthIndex] || month;
};

export const calculateCompletionRate = (answers: AnswerQuestionKPI[]): number => {
  if (!answers || answers.length === 0) {
    return 0;
  }

  const validAnswers = answers.filter((answer) => answer.answer !== 'N/A');
  if (validAnswers.length === 0) {
    return 0;
  }

  const { sum, count } = validAnswers.reduce(
    (acc, question) => {
      if (question.answer === 'Да') {
        acc.sum += 100;
        acc.count += 1;
      } else if (question.answer === 'Нет') {
        acc.sum += 0;
        acc.count += 1;
      }
      return acc;
    },
    { sum: 0, count: 0 }
  );

  return count > 0 ? Math.round(sum / count) : 0;
};

export const transformReportAnswersToQuestions = (reportAnswers: ReportAnswerKPI[]): AnswerQuestionKPI[] => {
  return reportAnswers.map((answer) => ({
    answer: answer.answer as 'Да' | 'Нет' | 'N/A',
    department: answer.factory.title,
    factory: answer.factory.title,
    month: answer.month,
    year: answer.year,
    direction: answer.direction.Title,
    requirement: answer.requirement.Title,
    isFocal: answer.requirement.isFocal || false,
    answerId: answer.id,
  }));
};

export const processKPIDataByMonths = (reportAnswers: ReportAnswerKPI[], dateRange?: { startDate: string; endDate: string }): ProcessedKPIData => {
  if (!reportAnswers || reportAnswers.length === 0) {
    return getDefaultKPIData(dateRange);
  }

  const allAnswers = transformReportAnswersToQuestions(reportAnswers);

  let years: number[];
  if (dateRange?.startDate && dateRange?.endDate) {
    const startYear = parseInt(dateRange.startDate.split('-')[0]);
    const endYear = parseInt(dateRange.endDate.split('-')[0]);
    years = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);
  } else {
    years = [...new Set(reportAnswers.map((a) => a.year))].sort((a, b) => a - b);
  }

  const monthData = allAnswers.reduce((acc, answer) => {
    const monthKey = `${answer.month}-${answer.year}`;

    if (!acc[monthKey]) {
      acc[monthKey] = {
        month: answer.month!,
        year: answer.year!,
        label: formatMonth(answer.month!),
        answers: [],
      };
    }

    acc[monthKey].answers.push(answer);
    return acc;
  }, {} as Record<string, any>);

  const allMonths: any[] = [];
  years.forEach((year) => {
    for (let i = 0; i < 12; i++) {
      const monthStr = String(i + 1).padStart(2, '0');
      const monthKey = `${monthStr}-${year}`;
      allMonths.push(monthData[monthKey] || {
        month: monthStr,
        year,
        label: MONTH_NAMES[i],
        answers: [],
      });
    }
  });

  const data = allMonths.map((m: any) => {
    if (m.answers.length === 0) {
      return null;
    }
    const completionRate = calculateCompletionRate(m.answers);
    return {
      value: completionRate,
      label: m.label,
    };
  });

  const kpiPlan = allMonths.map((m) => ({ value: 0, label: m.label }));
  const kpiFact = allMonths.map((m) => ({ value: 0, label: m.label }));

  return { data, kpiPlan, kpiFact };
};

export const getDefaultKPIData = (dateRange?: { startDate: string; endDate: string }): ProcessedKPIData => {
  let labels: string[];
  if (dateRange?.startDate && dateRange?.endDate) {
    const startYear = parseInt(dateRange.startDate.split('-')[0]);
    const endYear = parseInt(dateRange.endDate.split('-')[0]);
    labels = [];
    for (let y = startYear; y <= endYear; y++) {
      MONTH_NAMES.forEach((name) => labels.push(name));
    }
  } else {
    labels = [...MONTH_NAMES];
  }
  return {
    data: Array(labels.length).fill(null),
    kpiPlan: labels.map((name) => ({ value: 0, label: name })),
    kpiFact: labels.map((name) => ({ value: 0, label: name })),
  };
};

export const transformFiltersToKPIApiFormat = (currentFilters: any, activeDirection: number | null, focusGop: any): KPIFilters => {
  const filters: KPIFilters = {};

  if (currentFilters?.locationKPI?.selectedIds?.length > 0) {
    filters.factories = currentFilters.locationKPI.selectedIds.map((id: number) => String(id));
  }

  if (currentFilters?.created?.startDate && currentFilters?.created?.endDate) {
    const startDate = currentFilters.created.startDate; 
    const endDate = currentFilters.created.endDate; 
    const [startYear, startMonth] = startDate.split('-').map((s: string) => parseInt(s));
    const [endYear, endMonth] = endDate.split('-').map((s: string) => parseInt(s));
    const months: string[] = [];
    const years: string[] = [];

    for (let year = startYear; year <= endYear; year++) {
      const monthStart = year === startYear ? startMonth : 1;
      const monthEnd = year === endYear ? endMonth : 12;

      for (let month = monthStart; month <= monthEnd; month++) {
        months.push(String(month).padStart(2, '0'));
        if (!years.includes(String(year))) {
          years.push(String(year));
        }
      }
    }

    filters.months = months;
    filters.years = years;
  } else {
    filters.months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    filters.years = [String(new Date().getFullYear())];
  }

  if (currentFilters?.direction?.selectedIds?.length > 0) {
    filters.directions = currentFilters.direction.selectedIds.map((id: number) => String(id));
  } else if (activeDirection) {
    filters.directions = [String(activeDirection)];
  }

  if (currentFilters?.formGop?.selectedIds?.length > 0) {
    filters.gops = currentFilters.formGop.selectedIds.map((id: number) => String(id));
  }

  if (currentFilters?.focusGop?.selected?.length > 0) {
    const selectedTitles = currentFilters.focusGop.selected;
    if (selectedTitles.length === 1) {
      const selectedFocalTitle = selectedTitles[0];
      const focalItem = focusGop?.find((focal: any) => focal.Title === selectedFocalTitle);
      if (focalItem && focalItem.Value !== undefined) {
        filters.isFocal = focalItem.Value;
      }
    }
  }

  if (currentFilters?.blockKPI?.selectedIds?.length > 0) {
    filters.blocks = currentFilters.blockKPI.selectedIds.map((id: number) => String(id));
  }

  if (currentFilters?.line?.selectedIds?.length > 0) {
    filters.departments = currentFilters.line.selectedIds.map((id: number) => String(id));
  }

  if (currentFilters?.kpiName?.selectedIds?.length > 0) {
    filters.kpiNames = currentFilters.kpiName.selectedIds.map((id: number) => String(id));
  }

  return filters;
};

export const getKPIAnswersStatistics = (reportAnswers: ReportAnswerKPI[]) => {
  if (!reportAnswers || reportAnswers.length === 0) {
    return { total: 0, completed: 0, notCompleted: 0, naCount: 0, completionRate: 0 };
  }

  const total = reportAnswers.length;
  const completed = reportAnswers.filter((a) => a.answer === 'Да').length;
  const notCompleted = reportAnswers.filter((a) => a.answer === 'Нет').length;
  const naCount = reportAnswers.filter((a) => a.answer === 'N/A').length;
  const validAnswers = total - naCount;

  const completionRate = validAnswers > 0 ? Math.round((completed * 100) / validAnswers) : 0;

  return { total, completed, notCompleted, naCount, completionRate };
};
